import Image from 'next/image';
import { Asset, Maybe, Organization } from '@/types/pbskids-graph';
import styles from './SponsorsFlexBox.module.scss';
import BridgeLink from '@/components/base/BridgeLink';

type OverrideClassNames = {
  sponsorList: string;
  oblongImage: string;
};

export type LogoSizes = {
  normalHeightLimits: number[];
  oblongWidthLimits: number[];
  breakpoint: number;
};

type SponsorsFlexProps = {
  title?: string;
  list: Organization[];
  logoTheme?: Maybe<string>;
  classNames?: OverrideClassNames;
  logoSizes?: LogoSizes;
};

const defaultLogoSizes: LogoSizes = {
  normalHeightLimits: [ 48, 60 ],
  oblongWidthLimits: [ 67, 84 ], // = normalHeightLimits * 1.4
  breakpoint: 568,
};

const image = (
  target: Organization,
  imageAsset: Asset,
  logoSizes: LogoSizes,
  classNames?: OverrideClassNames,
) => {
  // We want to impose height restrictions on sponsor images, but the Next.js Image component
  // only accepts width sizes. So we calculate the widths per image based on it's aspect ratio and target height.
  const aspectRatio = (imageAsset.width as number) / (imageAsset.height as number);

  // Target width and height limits are based on the aspect ratio.
  // The bounding box for logos is proportional to 560 x 400px, this results in a 1.4 aspect ratio.
  // For images where the aspect ratio is larger than 1.4, then consider these as oblong and restrict
  // these to max widths instead of target heights.
  const isOblong = aspectRatio > 1.4;
  let imageClass = '';
  let widths;
  let heights;

  if (isOblong) {
    imageClass = `${styles.oblongImage} ${classNames?.oblongImage}`;
    widths = logoSizes.oblongWidthLimits;
    heights = [
      widths[0] / aspectRatio,
      widths[1] / aspectRatio,
    ];
  } else {
    heights = logoSizes.normalHeightLimits;
    widths = [
      aspectRatio * heights[0],
      aspectRatio * heights[1],
    ];
  }

  const altText = target.title || '';

  const sizes = widths[1] === widths[0] ?
    `${widths[0]}px` :
    `(min-width: ${logoSizes.breakpoint}px) ${widths[1]}px, ${widths[0]}px`;

  return <Image
    className={ imageClass }
    width={widths[0]}
    height={heights[0]}
    src={imageAsset.url || ''}
    sizes={sizes}
    alt={altText}
  />;
};

const orgList = (
  org: Organization,
  index: number,
  logoTheme: Maybe<string>,
  logoSizes: LogoSizes,
  classNames?: OverrideClassNames,
) => {
  // If the logotheme is anything but 'light', we want to use the dark logo.
  const imageTheme = (logoTheme === 'dark') ? 'organizationDarkLogo' : 'organizationLightLogo';

  if (!org[imageTheme]?.[0]?.url) return null;

  const asset = org[imageTheme][0];

  return asset &&
    <li key={index}>
      {org.websiteUrl ?
        <BridgeLink
          href={org.websiteUrl || ''}
          title={org.title || ''}
          imageAltText={org.bridgeImage?.[0]?.altText || ''}
          modalBody={org.bridgeBodyText || ''}
          modalHeader={org.bridgeHeadline || ''}
          modalImage={org.bridgeImage?.[0]?.url || ''}
        >
          {image(org, asset, logoSizes, classNames)}
        </BridgeLink> :

        image(org, asset, logoSizes, classNames)
      }
    </li>;
};

export default function SponsorsFlexBox(
  { title, list, logoTheme, classNames, logoSizes = defaultLogoSizes }: SponsorsFlexProps,
) {
  return <aside className={`${styles.sponsorsList} ${classNames?.sponsorList}`}>
    {title && <h2 className={styles.sponsorsListTitle}>{title}:</h2>}

    <ul className={styles.sponsorsListBox}>
      { list.map((org, index) => orgList(org, index, logoTheme || '', logoSizes, classNames))}
    </ul>
  </aside>;
}
